<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import appConfig from "@/app.config";
import { mapGetters, mapActions, mapState } from "vuex";
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import Swal from "sweetalert2";
import moment from "moment";

export default {
  page: {
    title: "Invoice Details",
    meta: [
      {
        name: "description",
        content: appConfig.description,
      },
    ],
  },
  components: {
    Layout,
    PageHeader,
  },
  data() {
    return {
      title: "Invoice Details",
      items: [
        { text: "Home", href: "/" },
        { text: "Invoices", active: true },
      ],
      data: {
        id: atob(this.$route.params.id),
      },
      isGeneratingPdf: false,
      whatsappTargetPhone: '',
    };
  },
  computed: {
    ...mapState("notification", ["type", "message", "show"]),
    ...mapGetters("invoices", ["Invoice", "SaveStatus"]),
    ...mapGetters("setting", ["PaymentOptions", "ReceiptSettings"]),
    rows() {
      return this.Invoice ? this.Invoice.length : 1;
    },
    logoSource() {
        if (this.Invoice && this.Invoice.business.Businesslogo_base64) {
            return this.Invoice.business.Businesslogo_base64;
        }
        if (this.Invoice && this.Invoice.business.Businesslogo) {
            return this.Invoice.business.Businesslogo;
        }
        return null;
    }
  },

  watch: {
    show(newValue) {
      if (newValue == true) {
        if (this.type == "success") {
          Swal.fire("Success!", this.message.msg, "success");
        } else {
          Swal.fire("Error!", this.message.msg, "error");
        }
      }
    },
  },

  methods: {
    ...mapActions({
      FetchInvoice: "invoices/fetchInvoice",
      fetchReceiptSettings: "setting/fetchReceiptSettings",
      FetchModes: "setting/fetchPaymentOptions",
      notifySuccess: "notification/success",
      notifyError: "notification/error",
    }),

    thousandSeperator(m) {
      if (m !== null && m !== undefined) {
        return m.toString().replace(/\D/g, "").replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      }
      return m;
    },

    UpdateSetting(id, status) {
      let activeoption = this.PaymentOptions.find(item => item.id == id);
      if (activeoption) {
        activeoption.showstatus = status === 1 ? 0 : 1;
      }
    },

    formatPhoneNumber(phone) {
      if (!phone) return null;
      let dialcode = this.Invoice.business.country.dial_code;
      return dialcode + phone.toString().replace(/\D/g, '').slice(-9);
    },

    async generateAndSharePdf(shareType) {
      this.isGeneratingPdf = true;
      const invoiceElement = document.getElementById('invoice-content-wrapper');
      
      if (!invoiceElement) {
        this.notifyError({ msg: "Error: Could not find invoice content." });
        this.isGeneratingPdf = false;
        return;
      }

      try {
        // Add a class to hide unchecked payment options for PDF generation
        invoiceElement.classList.add('pdf-generating');
        
        const canvas = await html2canvas(invoiceElement, {
          scale: 2,
          useCORS: true,
          ignoreElements: (element) => element.classList.contains('d-print-none'),
        });
        
        const imgData = canvas.toDataURL('image/jpeg');
        const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4' });
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
        
        pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight);
        const invoiceFileName = `Invoice-${this.Invoice.invoice_number}-${this.Invoice.customer.name}-${moment(new Date()).format("YYYY-MM-DD")}.pdf`;

        if(shareType === 'app'){
          const pdfBlob = pdf.output('blob');
          const file = new File([pdfBlob], invoiceFileName, { type: 'application/pdf' });   
          if (navigator.share && navigator.canShare({ files: [file] })) {
            await navigator.share({
              title: `Invoice ${this.Invoice.invoice_number}`,
              text: `Hello ${this.Invoice.customer.name},\n\nPlease find your invoice attached.\n\nThank you!`,
              files: [file],
            });
          } 
        }else if (shareType === 'email') {
          pdf.save(invoiceFileName);
          const successMessage = "The invoice has been downloaded as "+invoiceFileName+". Please attach this file to your message.";
          this.notifySuccess({ msg: successMessage });
          const customerEmail = this.Invoice.customer.email;
          const subject = `Invoice ${this.Invoice.invoice_number} from ${this.Invoice.business.name}`;
          const body = `Hello ${this.Invoice.customer.name},\n\nPlease find our invoice attached.\n\nThank you!`;
          window.open(`mailto:${customerEmail || ''}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`);
        } else if (shareType === 'whatsapp') {
          pdf.save(invoiceFileName);
          const successMessage = "The invoice has been downloaded as "+invoiceFileName+". Please attach this file to your message.";
          this.notifySuccess({ msg: successMessage });
          const customerPhone = this.Invoice.customer.phone; 
          this.whatsappTargetPhone = this.formatPhoneNumber(customerPhone);
          if (this.whatsappTargetPhone) {
              this.$bvModal.show('share-whatsapp-modal');
          } else {
              this.notifyError({ msg: "Cannot share via WhatsApp because the customer does not have a contact number saved." });
          }
        }
      } catch (error) {
        this.notifyError({ msg: "An error occurred while generating the PDF. Please ensure you are connected to the internet." });
      } finally {
        // Always remove the class after PDF generation is complete or fails
        invoiceElement.classList.remove('pdf-generating');
        this.isGeneratingPdf = false;
      }
    },

    shareToWhatsApp(type) {
        const message = `Hello, please find attached Invoice ${this.Invoice.invoice_number}.`;
        const encodedMessage = encodeURIComponent(message);
        let url = '';
        if (this.whatsappTargetPhone) {
            url = type === 'app' 
                ? `whatsapp://send?phone=${this.whatsappTargetPhone}&text=${encodedMessage}` 
                : `https://web.whatsapp.com/send?phone=${this.whatsappTargetPhone}&text=${encodedMessage}`;
            if (type === 'app') {
                window.location.href = url;
            } else {
                window.open(url, '_blank');
            }
        }
        this.$bvModal.hide('share-whatsapp-modal');
    },
  },
  async created() {
    try {
      await this.FetchInvoice(this.data);
      this.fetchReceiptSettings();
      this.FetchModes();
    } catch (error) {
      this.notifyError({ msg: "Failed to initialize invoice details: " + error });
    }
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" class="d-print-none"/>
    <div v-if="isGeneratingPdf" class="pdf-loading-overlay">
      <div class="spinner-border text-light" role="status"></div>
      <span class="text-light ms-2">Generating PDF...</span>
    </div>
    <div class="row mt-5 px-2" v-if="Invoice">
      <div class="col-lg-12">
         <div class="card px-3" id="invoice-content-wrapper">
          <div class="card-body" id="invoice-content">
            <div class="invoice-title">
              <div class="row d-print-none mb-3">
                <div class="col-12 text-end">
                    <div class="btn-group me-2">
                      <button type="button" class="btn btn-info dropdown-toggle" data-bs-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                        <i class="fa fa-share-alt"></i> Share <i class="mdi mdi-chevron-down"></i>
                      </button>
                      <div class="dropdown-menu">
                        <a class="dropdown-item" href="#" @click="generateAndSharePdf('app')"><i class="fa fa-desktop me-2"></i> Any App</a>
                        <a class="dropdown-item" href="#" @click="generateAndSharePdf('email')"><i class="fa fa-envelope me-2"></i> Email</a>
                        <a class="dropdown-item" href="#" @click="generateAndSharePdf('whatsapp')"><i class="fab fa-whatsapp me-2"></i> WhatsApp</a>
                      </div>
                    </div>
                    <a href="javascript:window.print()" class="btn btn-success waves-effect waves-light me-2">
                      <i class="fa fa-print"></i> Print
                    </a>
                    <b-button @click="$router.go(-1)" variant="warning"><i class="fa fa-arrow-left"></i> Back</b-button>
                </div>
              </div>
              <div class="row">
                <div class="col-md-3" align="center">
                  <img v-if="logoSource" :src="logoSource" height="80" crossorigin="anonymous" />
                </div>
                <div class="col-md-9 text-center">
                  <span class="font-size-24 text-uppercase text-success"><strong>{{Invoice ? Invoice.business.name : ''}}</strong></span><br/>
                  <span class="font-size-18"><b>{{Invoice ? Invoice.branch.address : ''}}</b></span><br/>
                  <span class="font-size-18"><strong>Tel: </strong><b>{{Invoice?Invoice.branch.phone1: ''}}</b></span><br/>
                  <span class="font-size-18" v-if="Invoice && Invoice.branch.email"><strong>Email: </strong><b>{{Invoice ? Invoice.branch.email : "---"}}</b></span><br/>
                </div>
              </div>
            </div>
            <div style="border-top: 1px solid black;"></div>
            <div class="font-size-24 text-center py-3"><b>CUSTOMER INVOICE</b></div>
            <div class="row font-size-17 py-3">
              <div class="col-6">
                <address><strong>Customer:</strong> {{ Invoice?Invoice.customer.name:'' }} - {{ Invoice?Invoice.customer.phone:'' }}</address>
              </div>
              <div class="col-6">
                <address><strong>Date:</strong> {{Invoice.date}} <br /><strong>Invoice Number:</strong> {{ Invoice?Invoice.invoice_number:'' }}</address>
              </div>
            </div>
            <div style="border-top: 1px solid black;"></div>
            <div class="p-2 mt-0"><h3 class="font-size-16">Invoice Items</h3></div>
            <div>
              <table class="table font-size-15">
                <thead>
                  <tr>
                    <th style="width: 70px">No.</th>
                    <th>Product</th>
                    <th>Quantity</th>
                    <th class="text-sm-end">Price</th>
                    <th class="text-sm-end">Total</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(pdt, index) in Invoice?Invoice.items:'---'" :key="index">
                    <td>{{ index+1 }}</td>
                    <td>{{pdt.product}}</td>
                    <td>{{pdt.qty}} {{pdt.unit}}</td>
                    <td class="text-sm-end">{{pdt.price.toLocaleString()}}</td>
                    <td class="text-sm-end">{{pdt.total}}</td>
                  </tr>
                </tbody>
              </table>
              <div class="invoice-summary-section">
                <div class="summary-row">
                  <span class="summary-title">Total: </span>
                  <span>{{Invoice ? Invoice.currency : ""}} {{ Invoice?Invoice.total.toLocaleString():0}}</span>
                </div>
                <div class="summary-row">
                  <span class="summary-title">Paid: </span>
                  <span>{{Invoice ? Invoice.currency : ""}} {{ Invoice?Invoice.paid.toLocaleString():0}}</span>
                </div>
                <div class="summary-row total-row">
                  <span class="summary-title">Balance: </span>
                  <span>{{Invoice ? Invoice.currency : ""}} {{ Invoice?(Invoice.total-Invoice.paid).toLocaleString():0}}</span>
                </div>
                <div class="summary-row total-row" v-if="ReceiptSettings.indicate_customerbalance == 1 && Invoice.customer.name">
                  <span class="summary-title">Total Balance Due: </span>
                  <span>{{Invoice ? Invoice.currency : ""}} {{ Invoice && Invoice.customer ? (-Invoice.customer.balance || 0).toLocaleString() : "0" }}</span>
                </div>
              </div>
              <div style="border-top: 1px solid black;" class="mt-3"></div>
              <div class="row py-3">
                <h5 class="text-center"><b>PAYMENTS CAN BE MADE VIA: </b></h5>
                <table class="table font-size-15 payment-methods">
                <thead>
                  <tr>
                    <th>Location</th>
                    <th>Account Type</th>                    
                    <th>Account Name</th>
                    <th>Account Number</th>
                    <th class="d-print-none">Show</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in PaymentOptions" :key="item.id" :class="{ 'payment-option-hidden-on-print': item.showstatus != 1 }">
                    <td>{{item.type_name}}</td>
                    <td>{{item.type}}</td>                    
                    <td>{{item.name}}</td>
                    <td>{{item.anumber}}</td>
                    <td class="d-print-none">
                      <div class="form-check form-switch form-switch-lg">
                        <input
                          @change="UpdateSetting(item.id,item.showstatus)"
                          class="form-check-input"
                          type="checkbox"
                          :checked="item.showstatus==1"
                        />
                      </div>
                    </td>
                  </tr>
                </tbody>
                </table>
              </div>
              <div style="border-top: 1px solid black;"></div>
              <div class="font-size-14 py-4 row">
                <div class="col-md-6"><span>{{Invoice ? Invoice.business.tagline : ''}}</span></div>
                <div class="col-md-6" align="right"><strong>Prepared by:</strong> <span>{{Invoice ? Invoice.user : ''}}</span></div>
              </div>
            </div>
            <!-- modal share invoice via WhatsApp -->
            <b-modal
                header-bg-variant="info"
                header-text-variant="light"
                body-text-variant="info"
                footer-bg-variant="info"
                footer-text-variant="info"
                id="share-whatsapp-modal"
                hide-footer
            >
              <template #modal-title> Share Invoice Via WhatsApp</template>
              <div class="modal-content">
                <div class="modal-body">
                  <p>The invoice PDF has been downloaded to your device.</p>
                  <p class="mb-0">How would you like to open WhatsApp to share it?</p>
                </div>
                <div class="modal-footer d-flex justify-content-center">
                  <button type="button" class="btn btn-success" @click="shareToWhatsApp('app')">
                    <i class="fab fa-whatsapp me-2"></i>Open WhatsApp App
                  </button>
                  <button type="button" class="btn btn-secondary" @click="shareToWhatsApp('web')">
                    <i class="fa fa-desktop me-2"></i>Use WhatsApp Web
                  </button>
                </div>
              </div>
            </b-modal>
            <!-- modal ends -->
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<style>
.invoice-summary-section .summary-row {
  display: flex;
  justify-content: flex-end;
  font-size: 13pt;
  margin-bottom: 5px;
}
.invoice-summary-section .summary-row span:first-child {
  margin-right: 20px;
  font-weight: bold; /* Replaces .summary-title */
}
.invoice-summary-section .total-row {
  font-weight: bold;
}

/* Style to hide unchecked payment options during PDF generation */
.pdf-generating .payment-option-hidden-on-print {
  display: none !important;
}

/* --- STYLES FOR AUTOMATIC PRINT FORMATTING --- */
@media print {
  * { box-sizing: border-box; }
  .left-side-menu, .navbar-custom, .footer, .page-header, .d-print-none { display: none !important; }
  .payment-option-hidden-on-print { display: none !important; }

  body { background-color: #fff !important; }
  .main-content, .page-content, .container-fluid { width: 100% !important; margin: 0 !important; padding: 0 !important; }
  #invoice-content-wrapper { border: none !important; box-shadow: none !important; padding: 0 !important; }

  /* --- FORMAT 1: Standard A4/A3/A5 Styles --- */
  /* This view will have NO vertical borders */
  @media (min-width: 150mm) {
    @page { margin: 15mm; }
    #invoice-content { font-size: 10pt !important; }
    #invoice-content .font-size-24 { font-size: 18pt !important; }
    #invoice-content .font-size-18 { font-size: 12pt !important; }
    #invoice-content .table tr { page-break-inside: avoid; }
    
    /* Let global styles handle summary, just adjust font for print */
    .invoice-summary-section .summary-row { font-size: 11pt !important; }
    .invoice-summary-section .total-row { font-size: 12pt !important; }
  }

  /* --- FORMAT 2: Receipt Printer Styles --- */
  /* This view WILL HAVE vertical borders */
  @media (max-width: 100mm) {
    @page { margin: 0; }
    #invoice-content {
      width: 100% !important; padding: 0 3mm !important; 
      font-family: 'Courier New', Courier, monospace !important; font-size: 9pt !important; color: #000 !important;
      overflow-wrap: break-word; word-wrap: break-word; 
    }
    #invoice-content .row:not(.d-print-none) { display: block !important; }
    #invoice-content [class*="col-"]:not(.d-print-none) { width: 100% !important; padding: 0 !important; text-align: left !important; }
    #invoice-content .text-center { text-align: center !important; }
    #invoice-content .text-sm-end { text-align: right !important; }
    #invoice-content img { max-width: 100% !important; height: auto !important; margin-bottom: 5px; }
    #invoice-content .font-size-24 { font-size: 12pt !important; font-weight: bold; }
    #invoice-content .font-size-18 { font-size: 10pt !important; line-height: 1.2; }
    #invoice-content h3, #invoice-content h5 { font-size: 10pt !important; font-weight: bold; }
    
    /* MODIFIED: Add border-collapse to ALL tables */
    #invoice-content .table { font-size: 8pt !important; table-layout: fixed; width: 100%; border-collapse: collapse; }
    
    /* MODIFIED: Add vertical borders to ALL table cells */
    #invoice-content .table th, #invoice-content .table td {
      padding: 3px 2px !important;
      border-bottom: 1px dotted #000 !important;
      border-right: 1px dotted #000; /* Add right border */
      word-wrap: break-word;
    }
    /* ADDED: Rule to add the leftmost border to ALL tables */
    #invoice-content .table th:first-child, #invoice-content .table td:first-child {
      border-left: 1px dotted #000; /* Add left border */
    }

    /* Precise Column Width for ITEMS table */
    #invoice-content .font-size-15 th:nth-child(1), #invoice-content .font-size-15 td:nth-child(1) { width: 10% !important; }
    #invoice-content .font-size-15 th:nth-child(2), #invoice-content .font-size-15 td:nth-child(2) { width: 30% !important; }
    #invoice-content .font-size-15 th:nth-child(3), #invoice-content .font-size-15 td:nth-child(3) { width: 20% !important; }
    #invoice-content .font-size-15 th:nth-child(4), #invoice-content .font-size-15 td:nth-child(4) { width: 20% !important; }
    #invoice-content .font-size-15 th:nth-child(5), #invoice-content .font-size-15 td:nth-child(5) { width: 20% !important; }
    
    /* Summary Section styling for receipt */
    .invoice-summary-section { margin-top: 10px; border-top: 1px dashed #000; padding-top: 5px; }
    .invoice-summary-section .summary-row { display: flex; justify-content: space-between; font-size: 10pt !important; margin-bottom: 0; }
    .invoice-summary-section .total-row { font-weight: bold; }

    /* Payment Methods Table styling for receipt */
    .payment-methods { font-size: 7pt !important; table-layout: auto;} /* Allow auto layout for this table */
    
    /* Final cleanup rules */
    #invoice-content .table thead tr { border-bottom: 1px solid #000 !important; }
    #invoice-content div[style*="border-top"] { border-top: 1px dashed black !important; margin: 5px 0 !important; }
    #invoice-content .py-5, #invoice-content .py-3, #invoice-content .p-2, #invoice-content .py-4 { padding-top: 5px !important; padding-bottom: 5px !important; }
  }
}
</style>